import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Card from "ds/components/Card";
import Tile from "ds/components/Tile";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";

import { SET_INFRA_ASSISTANT_BUILD_MODE_ENABLED, SET_INFRA_ASSISTANT_ENABLED } from "./gql";

type AiInfraAssistantCardProps = {
  infraAssistantEnabled: boolean;
  infraAssistantBuildModeEnabled: boolean;
};

const AiInfraAssistantCard = ({
  infraAssistantEnabled,
  infraAssistantBuildModeEnabled,
}: AiInfraAssistantCardProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationAi,
  });

  const [setInfraAssistantEnabled, { loading: toggleLoading }] = useMutation(
    SET_INFRA_ASSISTANT_ENABLED,
    { refetchQueries: ["GetAISettings"], awaitRefetchQueries: true }
  );

  const [setBuildModeEnabled, { loading: buildModeLoading }] = useMutation(
    SET_INFRA_ASSISTANT_BUILD_MODE_ENABLED,
    { refetchQueries: ["GetAISettings"], awaitRefetchQueries: true }
  );

  const handleToggle = useCallback(async () => {
    const newEnabled = !infraAssistantEnabled;

    try {
      const { errors } = await setInfraAssistantEnabled({
        variables: { enabled: newEnabled },
      });
      if (errors?.length) throw errors;
      trackSegmentAnalyticsEvent("Infra Assistant Toggled", {
        status: newEnabled ? "enabled" : "disabled",
      });
      reportSuccess({
        message: newEnabled ? "Infra Assistant enabled" : "Infra Assistant disabled",
      });
    } catch (e) {
      onError(e);
    }
  }, [infraAssistantEnabled, onError, reportSuccess, setInfraAssistantEnabled, trackSegmentAnalyticsEvent]);

  const handleSetBuildMode = useCallback(
    async (enabled: boolean) => {
      if (enabled === infraAssistantBuildModeEnabled) return;

      try {
        const { errors } = await setBuildModeEnabled({
          variables: { enabled },
        });
        if (errors?.length) throw errors;
        trackSegmentAnalyticsEvent("Infra Assistant Build Mode Changed", {
          mode: enabled ? "ask_and_build" : "ask_only",
        });
        reportSuccess({
          message: enabled ? "Ask and build mode enabled" : "Ask-only mode enabled",
        });
      } catch (e) {
        onError(e);
      }
    },
    [infraAssistantBuildModeEnabled, onError, reportSuccess, setBuildModeEnabled, trackSegmentAnalyticsEvent]
  );

  return (
    <Card
      title={
        <Box direction="column" gap="small">
          <Typography tag="h3" variant="p-t6">
            Infra Assistant
          </Typography>
          <Typography tag="p" variant="p-body3-multiline" color="secondary">
            AI-powered chat for infrastructure questions and resource management.
          </Typography>
        </Box>
      }
      contentGap="medium"
    >
      <Toggle
        variant="switch"
        onChange={handleToggle}
        isSelected={infraAssistantEnabled}
        isDisabled={toggleLoading}
        align="center"
        aria-label="Enable Infra Assistant"
      >
        <Typography tag="p" variant="p-t8">
          Enable Infra Assistant
        </Typography>
      </Toggle>

      {infraAssistantEnabled && (
        <Box gap="large">
          <Tile
            title="Ask and build mode"
            description="Users can ask questions and create or manage infrastructure resources through Infra Assistant."
            indicator="radio"
            selected={infraAssistantBuildModeEnabled}
            disabled={buildModeLoading}
            onClick={() => handleSetBuildMode(true)}
          />
          <Tile
            title="Ask-only mode"
            description="Users can ask questions about infrastructure and Spacelift. Resource creation and management are disabled."
            indicator="radio"
            selected={!infraAssistantBuildModeEnabled}
            disabled={buildModeLoading}
            onClick={() => handleSetBuildMode(false)}
          />
        </Box>
      )}
    </Card>
  );
};

export default AiInfraAssistantCard;
